import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";
import { ChevronLeft } from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-brimi-cream">
      <Seo
        title="Terms of Service — BrimiH&G"
        description="The terms and conditions for using BrimiH&G, the AI study app for ICSE 2028 Class IX-X History, Civics and Geography."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto px-5 pt-8 pb-24 md:pb-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-brimi-navyText/70 hover:text-brimi-navy" data-testid="back-home">
          <ChevronLeft size={16} /> Home
        </Link>
        <h1 className="text-4xl font-heading font-bold text-brimi-navyText mt-4">Terms of Service</h1>
        <p className="text-sm text-brimi-navyText/60 mt-1">
          Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
        </p>

        <div className="mt-8 text-brimi-navyText/85 leading-relaxed space-y-6 markdown-body">
          <section>
            <h2>1. Acceptance</h2>
            <p>
              By creating an account or using BrimiH&amp;G (&ldquo;the Service&rdquo;), you agree to these Terms and
              our <Link to="/privacy" className="text-brimi-navy font-semibold">Privacy Policy</Link>. If you don&apos;t
              agree, please don&apos;t use the Service.
            </p>
          </section>

          <section>
            <h2>2. What we provide</h2>
            <p>
              BrimiH&amp;G provides AI-generated study material (chapter notes, MCQs, tutor chat), timed mock tests,
              an India + World map quiz, and progress tracking, scoped to the ICSE 2028 Class IX–X syllabus for
              History &amp; Civics and Geography.
            </p>
            <p>
              The AI-generated content is for study assistance only. It may occasionally be inaccurate or incomplete.
              Always verify important facts against your official textbook before an examination.
            </p>
          </section>

          <section>
            <h2>3. Your account</h2>
            <ul>
              <li>You must provide accurate signup information and keep your password secret.</li>
              <li>You are responsible for activity under your account.</li>
              <li>One person, one account. Sharing your account violates these Terms.</li>
            </ul>
          </section>

          <section>
            <h2>4. Subscriptions, trial &amp; payment</h2>
            <ul>
              <li>New users get a <strong>20-day free trial</strong> with full access. No card required.</li>
              <li>After the trial, you may subscribe via Razorpay at <strong>₹299 / month</strong> or <strong>₹2,499 / year</strong>.</li>
              <li>Plans are non-auto-renewing — you re-subscribe when the period ends.</li>
              <li>Prices are inclusive of applicable taxes unless stated otherwise.</li>
            </ul>
          </section>

          <section>
            <h2>5. Refund policy</h2>
            <p>
              If you believe a payment was made in error, contact us within 7 days of the transaction at{" "}
              <a href="mailto:brajeshc56@gmail.com">brajeshc56@gmail.com</a>. Refunds for digital subscriptions are at
              our discretion and are typically not granted after content has been consumed beyond a token amount.
            </p>
          </section>

          <section>
            <h2>6. Acceptable use</h2>
            <p>Don&apos;t do any of the following while using BrimiH&amp;G:</p>
            <ul>
              <li>Scrape, copy, or redistribute content from the app at scale.</li>
              <li>Attempt to break, reverse-engineer, or interfere with the service.</li>
              <li>Use the AI tutor for content unrelated to ICSE study (off-topic prompts may be limited or blocked).</li>
              <li>Upload abusive, illegal, or copyrighted material via the Owner-PDF feature unless you are the owner.</li>
            </ul>
            <p>We may suspend accounts that violate these rules.</p>
          </section>

          <section>
            <h2>7. Intellectual property</h2>
            <p>
              The BrimiH&amp;G brand, code, design, and curated content belong to us. AI-generated chapter notes and
              MCQs are provided to you for personal study under a non-exclusive, non-transferable, revocable licence.
              You may not republish them publicly without permission.
            </p>
          </section>

          <section>
            <h2>8. Disclaimer &amp; limitation of liability</h2>
            <p>
              The Service is provided &ldquo;as is&rdquo;. We do not warrant that AI-generated content is error-free or
              that the Service will be uninterrupted. To the maximum extent permitted by law, our total liability for
              any claim related to the Service is limited to the amount you paid us in the 12 months before the claim.
            </p>
          </section>

          <section>
            <h2>9. Termination</h2>
            <p>
              You can stop using BrimiH&amp;G at any time by closing your account. We may suspend or terminate accounts
              that violate these Terms.
            </p>
          </section>

          <section>
            <h2>10. Governing law</h2>
            <p>
              These Terms are governed by the laws of India. Any dispute will be subject to the jurisdiction of the
              courts in India.
            </p>
          </section>

          <section>
            <h2>11. Contact</h2>
            <p>
              For questions about these Terms, email{" "}
              <a href="mailto:brajeshc56@gmail.com">brajeshc56@gmail.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
